import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { UploadBtn } from "@/components/admin/SermonForm";
import { fileUrl } from "@/lib/api";

const lbl = "mb-1 block font-ui text-[10px] uppercase tracking-widest text-[#5F6875]";

// Photos of a slideshow: upload many at once, reorder with the arrows, remove with the X.
export const SlideshowFields = ({ b, onChange }) => {
  const latest = useRef(b);
  latest.current = b;
  const urls = useRef(b.urls || []);
  useEffect(() => { urls.current = b.urls || []; }, [b.urls]);
  const setUrls = (list) => {
    urls.current = list;
    onChange({ ...latest.current, urls: list });
  };
  const move = (i, d) => {
    const l = [...urls.current];
    [l[i], l[i + d]] = [l[i + d], l[i]];
    setUrls(l);
  };
  const list = b.urls || [];
  const t = `ve-block-${b.id}`;
  return (
    <div className="grid gap-2">
      <UploadBtn accept="image/*" multiple testid={`${t}-slides-upload`} label={list.length ? "Add more photos" : "Upload photos (pick several)"} onDone={(u) => setUrls([...urls.current, u])} />
      {list.length > 0 && <p className="font-ui text-[11px] text-[#5F6875]">{list.length} photo(s). Use the arrows to change the order.</p>}
      <div className="grid grid-cols-3 gap-2">
        {list.map((u, i) => (
          <div key={`${u}-${i}`} data-testid={`${t}-slide-${i}`} className="relative overflow-hidden rounded-lg border border-[#14213D]/10 bg-white">
            <img src={fileUrl(u)} alt="" className="aspect-[4/3] w-full object-cover" />
            <div className="flex items-center justify-between px-1 py-0.5">
              <button type="button" data-testid={`${t}-slide-left-${i}`} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move earlier" className="rounded p-0.5 text-[#14213D] disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5" /></button>
              <span className="font-mono text-[10px] text-[#5F6875]">{i + 1}</span>
              <button type="button" data-testid={`${t}-slide-right-${i}`} disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label="Move later" className="rounded p-0.5 text-[#14213D] disabled:opacity-30"><ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
            <button type="button" data-testid={`${t}-slide-remove-${i}`} onClick={() => setUrls(urls.current.filter((_, j) => j !== i))} aria-label="Remove photo" className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full shadow" style={{ backgroundColor: "#FFFFFF", color: "#B4442A" }}><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const LINES = [["solid", "Solid"], ["dotted", "Dotted"], ["dashed", "Dashed"]];

// Divider look: line style, color and thickness. Waits a moment before saving while the slider or color picker moves.
export const DividerFields = ({ b, onChange }) => {
  const [s, setS] = useState({ line: b.line || "solid", color: b.color || "#C8862F", thick: b.thick || 2 });
  const latest = useRef(b);
  latest.current = b;
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    const timer = setTimeout(() => onChange({ ...latest.current, ...s }), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s]);
  const t = `ve-block-${b.id}`;
  return (
    <div className="grid gap-3">
      <div>
        <span className={lbl}>Line style</span>
        <div className="grid grid-cols-3 gap-1.5">
          {LINES.map(([k, l]) => (
            <button type="button" key={k} data-testid={`${t}-line-${k}`} aria-pressed={s.line === k} onClick={() => setS({ ...s, line: k })}
              className={`rounded-lg border px-2 py-2 font-ui text-[11px] ${s.line === k ? "border-[#C8862F] bg-[#C8862F]/15 text-[#14213D]" : "border-[#14213D]/15 bg-white text-[#5F6875]"}`}>
              <span className="mb-1 block" style={{ borderTop: `2px ${k} #14213D` }} />{l}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className={lbl}>Color</span>
          <div className="flex items-center gap-1.5">
            <input type="color" data-testid={`${t}-divider-color`} value={s.color} onChange={(e) => setS({ ...s, color: e.target.value })} className="h-8 w-10 cursor-pointer rounded border border-[#14213D]/15 bg-white p-0.5" />
            <span className="font-mono text-[11px] text-[#4A5568]">{s.color}</span>
          </div>
        </div>
        <div>
          <span className={lbl}>Thickness: {s.thick}px</span>
          <input type="range" min="1" max="12" data-testid={`${t}-divider-thick`} value={s.thick} onChange={(e) => setS({ ...s, thick: Number(e.target.value) })} className="w-full accent-[#C8862F]" />
        </div>
      </div>
      <div className="rounded-lg bg-white px-3 py-4"><hr style={{ border: 0, borderTop: `${s.thick}px ${s.line} ${s.color}` }} /></div>
    </div>
  );
};
